import React, { useState, useEffect } from 'react';

interface Video {
    url: string;
    platform: 'youtube' | 'vimeo' | 'direct';
    videoId: string;
    title: { en?: string; sv?: string };
    thumbnail: string;
    isFeatured: boolean;
    order: number;
}

interface VirtualTour {
    url: string;
    platform: string;
    embedCode: string;
    title: { en?: string; sv?: string };
    thumbnail: string;
    isFeatured: boolean;
}

interface HotelVideoManagerProps {
    hotelId: string;
    language?: 'en' | 'sv';
}

const HotelVideoManager: React.FC<HotelVideoManagerProps> = ({ hotelId, language = 'en' }) => {
    const [videos, setVideos] = useState<Video[]>([]);
    const [virtualTours, setVirtualTours] = useState<VirtualTour[]>([]);
    const [loading, setLoading] = useState(true);
    const [newVideoUrl, setNewVideoUrl] = useState('');
    const [newVideoTitle, setNewVideoTitle] = useState('');
    const [newTourUrl, setNewTourUrl] = useState('');
    const [newTourTitle, setNewTourTitle] = useState('');
    const [activeTab, setActiveTab] = useState<'videos' | 'tours'>('videos');
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3000);
    };

    const fetchMedia = async () => {
        try {
            const res = await fetch(`/api/hotels/${hotelId}/media`);
            const data = await res.json();
            if (data.success) {
                setVideos(data.media.videos || []);
                setVirtualTours(data.media.virtualTours || []);
            }
        } catch (error) {
            console.error('Error fetching media:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchMedia();
    }, [hotelId]);

    const addVideo = async () => {
        if (!newVideoUrl) return;

        try {
            const res = await fetch(`/api/hotels/${hotelId}/media/video`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    url: newVideoUrl,
                    title: { [language]: newVideoTitle },
                    platform: newVideoUrl.includes('vimeo') ? 'vimeo' : 'youtube'
                })
            });
            const data = await res.json();
            if (data.success) {
                setVideos([...videos, data.video]);
                setNewVideoUrl('');
                setNewVideoTitle('');
                showMessage('success', language === 'sv' ? 'Video tillagd!' : 'Video added!');
            } else {
                showMessage('error', data.error);
            }
        } catch (error) {
            showMessage('error', 'Failed to add video');
        }
    };

    const deleteVideo = async (index: number) => {
        try {
            const res = await fetch(`/api/hotels/${hotelId}/media/video/${index}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                setVideos(videos.filter((_, i) => i !== index));
                showMessage('success', language === 'sv' ? 'Video borttagen' : 'Video deleted');
            }
        } catch (error) {
            showMessage('error', 'Failed to delete video');
        }
    };

    const addVirtualTour = async () => {
        if (!newTourUrl) return;

        try {
            const res = await fetch(`/api/hotels/${hotelId}/media/virtual-tour`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    url: newTourUrl,
                    title: { [language]: newTourTitle },
                    platform: newTourUrl.includes('matterport') ? 'matterport' : 'custom'
                })
            });
            const data = await res.json();
            if (data.success) {
                setVirtualTours([...virtualTours, data.tour]);
                setNewTourUrl('');
                setNewTourTitle('');
                showMessage('success', language === 'sv' ? '3D-tur tillagd!' : 'Virtual tour added!');
            }
        } catch (error) {
            showMessage('error', 'Failed to add virtual tour');
        }
    };

    const deleteVirtualTour = async (index: number) => {
        try {
            const res = await fetch(`/api/hotels/${hotelId}/media/virtual-tour/${index}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                setVirtualTours(virtualTours.filter((_, i) => i !== index));
                showMessage('success', language === 'sv' ? '3D-tur borttagen' : 'Virtual tour deleted');
            }
        } catch (error) {
            showMessage('error', 'Failed to delete virtual tour');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Message Toast */}
            {message && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${message.type === 'success' ? 'bg-green-600' : 'bg-red-600'} text-white`}>
                    {message.text}
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-700 pb-2">
                <button
                    onClick={() => setActiveTab('videos')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'videos' ? 'bg-teal-600 text-white' : 'text-gray-400 hover:text-white hover:bg-slate-700'}`}
                >
                    <span className="material-icons text-lg align-middle mr-2">play_circle</span>
                    {language === 'sv' ? 'Videor' : 'Videos'} ({videos.length})
                </button>
                <button
                    onClick={() => setActiveTab('tours')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'tours' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white hover:bg-slate-700'}`}
                >
                    <span className="material-icons text-lg align-middle mr-2">360</span>
                    {language === 'sv' ? '3D-turer' : 'Virtual Tours'} ({virtualTours.length})
                </button>
            </div>

            {/* Videos Tab */}
            {activeTab === 'videos' && (
                <div className="space-y-4">
                    {/* Add Video Form */}
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                        <h4 className="text-sm font-medium text-gray-400 mb-3">
                            {language === 'sv' ? 'Lägg till YouTube/Vimeo-video' : 'Add YouTube/Vimeo Video'}
                        </h4>
                        <div className="flex gap-3">
                            <input
                                type="text"
                                value={newVideoUrl}
                                onChange={(e) => setNewVideoUrl(e.target.value)}
                                placeholder="https://youtube.com/watch?v=..."
                                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400"
                            />
                            <input
                                type="text"
                                value={newVideoTitle}
                                onChange={(e) => setNewVideoTitle(e.target.value)}
                                placeholder={language === 'sv' ? 'Titel (valfritt)' : 'Title (optional)'}
                                className="w-48 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400"
                            />
                            <button
                                onClick={addVideo}
                                disabled={!newVideoUrl}
                                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {language === 'sv' ? 'Lägg till' : 'Add'}
                            </button>
                        </div>
                    </div>

                    {/* Video Grid */}
                    {videos.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <span className="material-icons text-4xl mb-2">videocam_off</span>
                            <p>{language === 'sv' ? 'Inga videor tillagda ännu' : 'No videos added yet'}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {videos.map((video, index) => (
                                <div key={index} className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700 group">
                                    <div className="relative aspect-video">
                                        {video.thumbnail ? (
                                            <img src={video.thumbnail} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                                                <span className="material-icons text-4xl text-gray-500">play_circle</span>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <a
                                                href={video.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 bg-white/20 rounded-full hover:bg-white/30"
                                            >
                                                <span className="material-icons text-white">play_arrow</span>
                                            </a>
                                            <button
                                                onClick={() => deleteVideo(index)}
                                                className="p-2 bg-red-600/80 rounded-full hover:bg-red-600"
                                            >
                                                <span className="material-icons text-white">delete</span>
                                            </button>
                                        </div>
                                        {video.isFeatured && (
                                            <span className="absolute top-2 left-2 px-2 py-0.5 bg-yellow-500 text-black text-xs font-bold rounded">
                                                Featured
                                            </span>
                                        )}
                                        <span className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded capitalize">
                                            {video.platform}
                                        </span>
                                    </div>
                                    <div className="p-3">
                                        <p className="text-sm text-white truncate">
                                            {video.title?.[language] || video.title?.en || `Video ${index + 1}`}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Virtual Tours Tab */}
            {activeTab === 'tours' && (
                <div className="space-y-4">
                    {/* Add Virtual Tour Form */}
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                        <h4 className="text-sm font-medium text-gray-400 mb-3">
                            {language === 'sv' ? 'Lägg till 3D-tur / Virtuell tur' : 'Add 3D/Virtual Tour'}
                        </h4>
                        <div className="flex gap-3">
                            <input
                                type="text"
                                value={newTourUrl}
                                onChange={(e) => setNewTourUrl(e.target.value)}
                                placeholder="https://my.matterport.com/show/?m=..."
                                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400"
                            />
                            <input
                                type="text"
                                value={newTourTitle}
                                onChange={(e) => setNewTourTitle(e.target.value)}
                                placeholder={language === 'sv' ? 'Titel (valfritt)' : 'Title (optional)'}
                                className="w-48 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400"
                            />
                            <button
                                onClick={addVirtualTour}
                                disabled={!newTourUrl}
                                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {language === 'sv' ? 'Lägg till' : 'Add'}
                            </button>
                        </div>
                    </div>

                    {/* Virtual Tours Grid */}
                    {virtualTours.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <span className="material-icons text-4xl mb-2">view_in_ar</span>
                            <p>{language === 'sv' ? 'Inga 3D-turer tillagda ännu' : 'No virtual tours added yet'}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {virtualTours.map((tour, index) => (
                                <div key={index} className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700 p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center">
                                                <span className="material-icons text-purple-400">360</span>
                                            </div>
                                            <div>
                                                <p className="text-white font-medium">
                                                    {tour.title?.[language] || tour.title?.en || `Virtual Tour ${index + 1}`}
                                                </p>
                                                <p className="text-sm text-gray-400 capitalize">{tour.platform}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <a
                                                href={tour.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 bg-slate-700 rounded-lg hover:bg-slate-600"
                                            >
                                                <span className="material-icons text-white">open_in_new</span>
                                            </a>
                                            <button
                                                onClick={() => deleteVirtualTour(index)}
                                                className="p-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30"
                                            >
                                                <span className="material-icons">delete</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HotelVideoManager;
